import React, { useState } from 'react';
import Button from '@/components/UI/Button';
import FormNumberInput from '@/components/UI/FormNumberInput';
import FormSelect from '@/components/UI/FormSelect';
import FormSwitch from '@/components/UI/FormSwitch';
import Modal from '@/components/UI/Modal';
import { buildDefaultStrategyLimit, type GovernanceStrategyLimitDraft } from './governanceHelpers';
import type { GovernanceStrategyOption } from './GovernanceStrategyLimitsTable';

interface GovernanceStrategyLimitModalProps {
    isOpen: boolean;
    onClose: () => void;
    existing: GovernanceStrategyLimitDraft | null;
    strategies: GovernanceStrategyOption[];
    usedStrategyIds: number[];
    onSave: (limit: GovernanceStrategyLimitDraft) => void;
}

const firstAvailableStrategyId = (strategies: GovernanceStrategyOption[], usedStrategyIds: number[]): number =>
    strategies.find((strategy) => !usedStrategyIds.includes(strategy.strategyId))?.strategyId ?? strategies[0]?.strategyId ?? 0;

const GovernanceStrategyLimitModal: React.FC<GovernanceStrategyLimitModalProps> = ({
    isOpen,
    onClose,
    existing,
    strategies,
    usedStrategyIds,
    onSave
}) => {
    const initialStrategyId = existing?.strategyId ?? firstAvailableStrategyId(strategies, usedStrategyIds);
    const [draft, setDraft] = useState<GovernanceStrategyLimitDraft>(() => existing ?? buildDefaultStrategyLimit(initialStrategyId));
    const openKey = isOpen ? (existing ? `edit:${existing.strategyId}` : `add:${initialStrategyId}`) : null;
    const [seededKey, setSeededKey] = useState<string | null>(null);

    if (openKey !== seededKey) {
        setSeededKey(openKey);

        if (openKey !== null) {
            setDraft(existing ?? buildDefaultStrategyLimit(initialStrategyId));
        }
    }

    const isEditing = existing !== null;
    const strategyOptions = strategies
        .filter((strategy) => strategy.strategyId === draft.strategyId || !usedStrategyIds.includes(strategy.strategyId))
        .map((strategy) => ({ value: strategy.strategyId, label: strategy.name }));
    const selectedStrategyName =
        strategies.find((strategy) => strategy.strategyId === draft.strategyId)?.name ?? `Strategy #${draft.strategyId}`;

    const handleSave = () => {
        if (draft.strategyId <= 0) {
            return;
        }

        onSave(draft);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} title={isEditing ? `Edit limits - ${selectedStrategyName}` : 'Add strategy limits'} onClose={onClose}>
            <div className="space-y-4">
                <FormSelect
                    disabled={isEditing}
                    helpText="Limits apply to this strategy across every entity it trades in the portfolio."
                    id="governanceStrategyLimitStrategy"
                    label="Subscribed strategy"
                    options={strategyOptions}
                    value={draft.strategyId}
                    onChange={(event) => setDraft({ ...draft, strategyId: Number.parseInt(event.target.value, 10) })}
                />

                <FormNumberInput
                    integer
                    helpText="Total open + counted positions this strategy may hold. Blank = no cap."
                    id="governanceStrategyLimitMaxPositions"
                    label="Max positions per strategy"
                    min={0}
                    placeholder="No cap"
                    value={draft.maxOpenPositionsPerStrategy}
                    onValueChange={(value) => setDraft({ ...draft, maxOpenPositionsPerStrategy: value })}
                />

                <div className="space-y-2 border-t border-zinc-800 pt-3">
                    <p className="text-sm text-zinc-400">Which existing exposure counts toward this limit:</p>
                    <FormSwitch
                        checked={draft.includePendingOrders}
                        id="governanceStrategyLimitIncludePending"
                        label="Count unfilled brokerage orders"
                        onChange={(event) => setDraft({ ...draft, includePendingOrders: event.target.checked })}
                    />
                    <FormSwitch
                        checked={draft.includeQueuedTrades}
                        id="governanceStrategyLimitIncludeQueued"
                        label="Count in-flight queued entries"
                        onChange={(event) => setDraft({ ...draft, includeQueuedTrades: event.target.checked })}
                    />
                    <FormSwitch
                        checked={draft.includeManualApprovalPending}
                        id="governanceStrategyLimitIncludeManualApproval"
                        label="Count trades awaiting manual approval"
                        onChange={(event) => setDraft({ ...draft, includeManualApprovalPending: event.target.checked })}
                    />
                </div>

                <div className="mt-4 flex justify-end space-x-2">
                    <Button variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={draft.strategyId <= 0} variant="primary" onClick={handleSave}>
                        {isEditing ? 'Save limits' : 'Add limits'}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default GovernanceStrategyLimitModal;
